// src/lib/buildtimeFn.ts
import { addFunctionSerializer } from "@vanilla-extract/css/functionSerializer";
import { style } from "@vanilla-extract/css";

// src/lib/utils.ts
function preventComposition(styleRule) {
  if (!styleRule || typeof styleRule === "string" || Array.isArray(styleRule)) {
    throw new Error("We only allow style objects inside of variants");
  }
  return styleRule;
}
function isStringOrNumber(obj) {
  return !!obj && (typeof obj === "string" || typeof obj === "number");
}
function isPrimitive(value) {
  return value !== Object(value);
}

// src/lib/createRuntimeFn.ts
function createRuntimeFn(buildResult) {
  const runtimeFn = (options) => {
    var _a, _b;
    const {
      variantClassNames,
      compoundVariants,
      baseClassName,
      initialCondition,
      responsiveVariantClassNames,
      defaultVariants
    } = buildResult;
    const selection = options ? { ...defaultVariants, ...options } : defaultVariants;
    const allVariantClassNames = { ...variantClassNames, ...responsiveVariantClassNames };
    const className = [baseClassName];
    for (const variantGroup in selection) {
      let variantOption = selection[variantGroup];
      if (variantOption === void 0) {
        variantOption = defaultVariants[variantGroup];
      }
      if (typeof variantOption === "boolean") {
        variantOption = variantOption === true ? "true" : "false";
      }
      const variantClassNameGroup = allVariantClassNames[variantGroup];
      if (!variantClassNameGroup)
        continue;
      if (isStringOrNumber(variantOption)) {
        className.push(((_a = variantClassNameGroup[variantOption]) == null ? void 0 : _a[initialCondition]) || "");
        continue;
      }
      for (const condition in variantOption) {
        let responsiveVariantOption = variantOption[condition];
        if (typeof responsiveVariantOption === "boolean") {
          responsiveVariantOption = responsiveVariantOption === true ? "true" : "false";
        }
        if (!responsiveVariantOption)
          continue;
        className.push(((_b = variantClassNameGroup[responsiveVariantOption]) == null ? void 0 : _b[condition]) || "");
      }
    }
    const matchingCompoundVariants = compoundVariants.filter(
      ([variants]) => Object.keys(variants).every((variantGroup) => Object.keys(selection).includes(variantGroup))
    );
    for (const [variants, classNamesPerCondition] of matchingCompoundVariants) {
      const selectionArray = Object.entries(selection);
      const filteredSelectionArray = selectionArray.filter(
        ([variantGroup]) => variantGroup in variants
      );
      const normalizedSelectionArray = filteredSelectionArray.map(
        ([variantGroup, variantOption]) => {
          return isPrimitive(variantOption) ? [variantGroup, { [initialCondition]: variantOption }] : [variantGroup, variantOption];
        }
      );
      const orderedConditions = Object.keys(classNamesPerCondition);
      const completedSelectionArray = normalizedSelectionArray.map(
        ([variantGroup, variantOption]) => {
          let optionValue = void 0;
          let addedVariantOptionWithConditions = {
            [initialCondition]: void 0,
            ...variantOption
          };
          for (const condition of orderedConditions) {
            const conditionOptionValue = variantOption[condition];
            optionValue = conditionOptionValue != null ? conditionOptionValue : optionValue;
            addedVariantOptionWithConditions[condition] = optionValue;
          }
          return [variantGroup, addedVariantOptionWithConditions];
        }
      );
      let wasEqual = false;
      for (const condition of orderedConditions) {
        const isEqual = completedSelectionArray.every(
          ([variantGroup, selectionObj]) => selectionObj[condition] === variants[variantGroup]
        );
        const shouldInclude = wasEqual;
        wasEqual = isEqual;
        if (shouldInclude && isEqual) {
          continue;
        }
        const compoundClassName = classNamesPerCondition[condition];
        if (isEqual && compoundClassName) {
          className.push(compoundClassName);
        }
      }
    }
    return className.join(" ");
  };
  return runtimeFn;
}

// src/lib/buildtimeFn.ts
function createRecipe({
  defaultConditions,
  initialCondition
}) {
  return (options, debugId) => {
    var _a, _b, _c;
    const {
      base = {},
      variants = {},
      responsiveVariants = {},
      conditions = defaultConditions,
      compoundVariants = [],
      defaultVariants = {}
    } = options;
    const config = {
      initialCondition: ((_a = options.initialCondition) != null ? _a : initialCondition) || "initial",
      conditions,
      responsiveVariantClassNames: {},
      variantClassNames: {},
      compoundVariants: [],
      defaultVariants,
      baseClassName: ""
    };
    config.baseClassName = typeof base === "string" ? base : style(base, debugId);
    for (const variantGroup in variants) {
      config.variantClassNames[variantGroup] = {};
      const group = config.variantClassNames[variantGroup];
      if (!group)
        continue;
      const variantOptions = variants[variantGroup];
      for (const variantOption in variantOptions) {
        const styleRule = preventComposition(variantOptions[variantOption]);
        group[variantOption] = {
          [config.initialCondition]: style(
            styleRule,
            debugId ? `${debugId}_${variantGroup}_${variantOption}` : `${variantGroup}_${variantOption}`
          )
        };
      }
    }
    for (const conditionName in conditions) {
      for (const variantGroup in responsiveVariants) {
        const variantOptions = responsiveVariants[variantGroup];
        for (const variantOption in variantOptions) {
          const styleRule = preventComposition(variantOptions[variantOption]);
          const mediaQuery = (_b = conditions[conditionName]) == null ? void 0 : _b["@media"];
          const debugName = debugId ? `${debugId}_${conditionName}_${variantGroup}_${variantOption}` : `${conditionName}_${variantGroup}_${variantOption}`;
          const className = mediaQuery ? style({ "@media": { [mediaQuery]: styleRule } }, debugName) : style(styleRule, debugName);
          const group = config.responsiveVariantClassNames[variantGroup] || {};
          const option = group[variantOption] || {};
          config.responsiveVariantClassNames[variantGroup] = {
            ...group,
            [variantOption]: {
              ...option,
              [conditionName]: className
            }
          };
        }
      }
    }
    for (const { style: theStyle, variants: theVariants } of compoundVariants) {
      const styleRule = preventComposition(theStyle);
      const compoundVariantClassNames = {};
      const debugName = Object.entries(theVariants).map(([key, value]) => `${key}_${value}`).join("_");
      const allRegularVariants = Object.keys(theVariants).every(
        (variantGroup) => Object.keys(variants).includes(variantGroup)
      );
      if (allRegularVariants) {
        const className = style(styleRule, `${debugId || ""}_compound_${debugName}`);
        compoundVariantClassNames[config.initialCondition] = className;
        config.compoundVariants.push([theVariants, compoundVariantClassNames]);
        continue;
      }
      for (const conditionName in conditions) {
        const mediaQuery = (_c = conditions[conditionName]) == null ? void 0 : _c["@media"];
        const responsiveStyleRule = mediaQuery ? { "@media": { [mediaQuery]: styleRule } } : styleRule;
        const className = style(
          responsiveStyleRule,
          `${debugId || ""}_compound_${debugName}_${conditionName}`
        );
        compoundVariantClassNames[conditionName] = className;
      }
      config.compoundVariants.push([theVariants, compoundVariantClassNames]);
    }
    const runtimeFn = createRuntimeFn(config);
    return addFunctionSerializer(runtimeFn, {
      importPath: "responsive-recipes",
      importName: "createRuntimeFn",
      args: [config]
    });
  };
}
export {
  createRecipe,
  createRuntimeFn,
  isPrimitive,
  isStringOrNumber,
  preventComposition
};
//# sourceMappingURL=index.js.map